import React, { useContext, useMemo, useState, useCallback, useRef } from "react";
import { Box, Stack, Typography, Card, CardContent, CircularProgress } from "@mui/material";
import { KeyboardArrowRight as ArrowRightIcon } from "@mui/icons-material";
import { WidgetContext } from "../../store/widgetContext";
import { formatTimeAgo, trimString } from "../../utils/common";
import { useSession } from "../../session/useSession";
import { SessionManager } from "../../session/SessionManager";
import AvatarStack from "../AvatarStack";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";

export default function Conversations({ handleNavigate, chatMode = "chat", isVoiceChatEnabled, botId, tenantId, projectUUID }) {
	const {
		agents, conversations, conversationsPagination,
		callConversations, callConversationsPagination,
		fetchMoreConversationsRef
	} = useContext(WidgetContext);
	const { sessionId } = useSession();
	const selectedConversations = chatMode === "chat" ? conversations : callConversations;
	const pagination = chatMode === "chat" ? conversationsPagination : callConversationsPagination;
	const { msg } = useWidgetMsg();
	const [loadingMore, setLoadingMore] = useState(false);
	const scrollContainerRef = useRef(null);

	const getTimeAgo = (timeStr) => {
		try {
			return formatTimeAgo(timeStr, msg);
		} catch (error) {
			console.error("@@ Error: getTimeAgo @@", error);
			return null;
		}
	};

	const processedConversations = useMemo(() => {
		if (!Array.isArray(selectedConversations) || !Array.isArray(agents)) return [];
		const filtered = chatMode === "chat" 
			? selectedConversations.filter((conv) => conv.placeholder !== true)
			: selectedConversations;

		const data = filtered.sort((a, b) => {
			const lastUpdatedA = new Date(a.records[a.records.length - 1]?.updatedAt || a.createdAt);
			const lastUpdatedB = new Date(b.records[b.records.length - 1]?.updatedAt || b.createdAt);
			return lastUpdatedB - lastUpdatedA;
		});

		return data.map((conv) => {
			const assignedAgents = [];
			const lastReplyRecord = [...conv.records].reverse().find(
				(record) => record?.reply?.message || record?.reply?.config?.message
			);

			let lastMessage = msg("conversations.no_reply", "No reply yet");
			if (lastReplyRecord?.reply) {
				const reply = lastReplyRecord.reply;
				if (reply.type === "image") {
					lastMessage = "Image";
				} else if (reply.message) {
					lastMessage = reply.message;
				} else if (reply.config?.message) {
					lastMessage = reply.config.message;
				}
			}

			conv.assignees?.forEach((agentIDaasId) => {
				const agent = agents.find((el) => el.agentIDaasId === agentIDaasId);
				if (agent) {
					assignedAgents.push(agent);
				}
			});

			return {
				conversationId: conv._id,
				assignees: conv.assignees || [],
				agents: assignedAgents,
				lastMsg: trimString(lastMessage, 31),
				time: getTimeAgo(lastReplyRecord?.updatedAt || conv.createdAt),
			};
		});
	}, [selectedConversations, agents, chatMode, msg]);

	const handleScroll = useCallback((e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollHeight - scrollTop - clientHeight < 100 && !loadingMore && pagination?.hasNextPage) {
			const nextPage = (pagination?.page || 1) + 1;
			const type = chatMode === "chat" ? "chats" : "calls";
			const contactId = SessionManager.getSocketContactId();
			if (fetchMoreConversationsRef?.current) {
				setLoadingMore(true);
				fetchMoreConversationsRef.current(contactId, botId, tenantId, projectUUID, type, sessionId, nextPage)
					.finally(() => setLoadingMore(false));
			}
		}
	}, [loadingMore, pagination, chatMode, botId, tenantId, projectUUID, sessionId, fetchMoreConversationsRef]);

	const handleConversationClick = (conversationId, assignedAgents) => {
		if (conversationId) {
			if (handleNavigate) {
				const location = chatMode === "chat" ? "newConversation" : "newVoiceChat";
				handleNavigate({ 
					location,
					conversationId,
					assignedAgents,
				});
			}
		} else {
			if (handleNavigate) {
				chatMode === "chat" 
					? handleNavigate({ location: "newConversation" }) 
					: handleNavigate({ location: "newVoiceChat" });
			}
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				borderRadius: "16px",
				overflow: "hidden",
			}}
		>
			<Box ref={scrollContainerRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column" }}>
				{processedConversations.length > 0 ? (
					<Stack spacing={1.5} sx={{ paddingBottom: isVoiceChatEnabled ? "150px" : "100px" }}>
						{processedConversations.map((conv, idx) => (
							<Card
								key={`conversation-item-${idx}`}
								sx={{
									background: "white",
									borderRadius: "16px",
									boxShadow: "none",
									border: "1px solid #E5E5E5",
									cursor: "pointer",
									"&:hover": { boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)" },
								}}
								onClick={() => handleConversationClick(conv.conversationId, conv.agents)}
							>
								<CardContent sx={{ padding: "16px !important" }}>
									<Stack direction="row" spacing={1.5} alignItems="flex-start">
										<AvatarStack agents={conv.agents || []} displayAgentInfo={false} />
										<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
											<Typography 
												variant="body2" 
												sx={{ 
													fontSize: "12px", 
													color: "black", 
													mb: 0.5,
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{conv.lastMsg}
											</Typography>
											<Typography 
												sx={{ 
													fontSize: "8px", 
													color: "#808080",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{conv.time || msg("time.just_now", "Just now")}
											</Typography>
										</Box>
										<ArrowRightIcon sx={{ fontSize: 16, color: "black", flexShrink: 0 }} />
									</Stack>
								</CardContent>
							</Card>
						))}
						{loadingMore && (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
								<CircularProgress size={24} />
							</Box>
						)}
					</Stack>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							minHeight: "200px",
							textAlign: "center",
						}}
					>
						<Typography variant="body2" sx={{ fontSize: "14px", color: "#808080", mb: 1 }}>
							{msg("conversations.no_conversations_yet", "No conversations yet")}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
}

